
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ChatbotCard from '@/components/chat/ChatbotCard';
import { ArrowRight, Sparkles, MessageCircle, Shield } from 'lucide-react';

// Sample chatbot data
const sampleChatbots = [
  {
    id: 'philosopher',
    name: 'The Philosopher',
    description: 'Engage in deep philosophical discussions about existence, knowledge, and morality.',
    tags: ['Philosophy', 'Ethics', 'Metaphysics'],
    avatarUrl: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?q=80&w=2080',
    conversations: 1204
  },
  {
    id: 'scientist',
    name: 'The Scientist',
    description: 'Discuss the latest in science, technology, and the natural world with clarity and depth.',
    tags: ['Science', 'Technology', 'Exploration'],
    avatarUrl: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2080',
    conversations: 986
  },
  {
    id: 'creative',
    name: 'The Creative',
    description: 'Explore art, literature, and creativity with an AI that understands the nuances of human expression.',
    tags: ['Art', 'Literature', 'Creativity'],
    avatarUrl: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=2080',
    conversations: 745
  }
];

const Index = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [animateHero, setAnimateHero] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const userToken = localStorage.getItem('userToken');
    setIsAuthenticated(!!userToken);
    
    // Start animations after component mounts
    setAnimateHero(true);
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 -z-10"></div>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className={`space-y-6 transition-all duration-1000 ${animateHero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-block">
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                Powered by Claude AI
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight">
              Learn with AI <span className="text-primary">Agents</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Engage with specialized learning agents, each with their own unique expertise and teaching style.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link to="/explore">
                <Button size="lg" className="rounded-full">
                  Explore Agents
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              {!isAuthenticated && (
                <Link to="/login">
                  <Button variant="outline" size="lg" className="rounded-full">
                    Sign in with Bluesky
                  </Button>
                </Link>
              )}
            </div>
          </div>
          
          <div className={`relative h-80 md:h-96 transition-all duration-1000 delay-300 ${animateHero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="absolute inset-0 grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-4">
                <Card className="glass animate-float shadow-lg h-40">
                  <CardContent className="p-4 h-full flex items-center justify-center">
                    <div className="text-center">
                      <MessageCircle size={28} className="mx-auto mb-2 text-primary" />
                      <p className="font-medium">Interactive Learning</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass shadow-lg h-52">
                  <CardContent className="p-4 h-full flex items-center justify-center">
                    <div className="text-center">
                      <Shield size={28} className="mx-auto mb-2 text-accent" />
                      <p className="font-medium">Secure Bluesky Auth</p>
                      <p className="text-sm text-muted-foreground mt-1">Connect with your social identity</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="flex flex-col gap-4 pt-8">
                <Card className="glass shadow-lg h-52">
                  <CardContent className="p-4 h-full flex items-center justify-center">
                    <div className="text-center">
                      <Sparkles size={28} className="mx-auto mb-2 text-primary" />
                      <p className="font-medium">Expert Agents</p>
                      <p className="text-sm text-muted-foreground mt-1">Specialized in various subjects</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass animate-float delay-700 shadow-lg h-40">
                  <CardContent className="p-4 h-full flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">Powered by</p>
                      <p className="font-medium">Claude AI</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Chatbots Section */}
      <section className="py-16 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold mb-4">Featured Learning Agents</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover our most popular AI learning companions and start your educational journey
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sampleChatbots.map((chatbot) => (
              <ChatbotCard
                key={chatbot.id}
                id={chatbot.id}
                name={chatbot.name}
                description={chatbot.description}
                tags={chatbot.tags}
                avatarUrl={chatbot.avatarUrl}
                conversations={chatbot.conversations}
              />
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link to="/explore">
              <Button variant="outline" size="lg" className="rounded-full">
                View All Agents
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Getting started with our AI learning agents is simple
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-sm relative">
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">1</div>
              <CardContent className="pt-12 pb-6">
                <h3 className="text-xl font-medium mb-2">Sign In with Bluesky</h3>
                <p className="text-muted-foreground">
                  Connect with your Bluesky social account to get started.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-sm relative">
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">2</div>
              <CardContent className="pt-12 pb-6">
                <h3 className="text-xl font-medium mb-2">Choose an Agent</h3>
                <p className="text-muted-foreground">
                  Browse our collection and select a learning agent that matches your interests.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-sm relative">
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">3</div>
              <CardContent className="pt-12 pb-6">
                <h3 className="text-xl font-medium mb-2">Start Learning</h3>
                <p className="text-muted-foreground">
                  Engage in interactive learning sessions with your chosen AI agent.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
